import type { ChangeEvent } from 'react';
import { useContext, useEffect, useState } from 'react';
import { Tooltip } from 'bootstrap';
import { SmartContext } from '../../../library/Core/SmartContext';
import { debounce, formatFullName, getDomainValueForCode } from '../../../library/Core/SmartFunctions';
import { jobPlace, parseCurrentJobs, pickDisplayJob } from '../../../library/Service/currentJobDisplay';
import EntrepreneurDirectoryService, { FIRST_GENERATION_CODE } from './EntrepreneurDirectoryService';
import { handleClearAllFilterClick } from './EntrepreneurSearchControl';

const imgClaimed = `${process.env.REACT_APP_IMAGE_BASEPATH}/claimed-entrepreneurs.svg`;
const imgConfirmed = `${process.env.REACT_APP_IMAGE_BASEPATH}/in-leadership-roles.svg`;
const imgCompanies = `${process.env.REACT_APP_IMAGE_BASEPATH}/in-top-companies.svg`;
const imgFirstGen = `${process.env.REACT_APP_IMAGE_BASEPATH}/shuttle.svg`;
const imgFinancial = `${process.env.REACT_APP_IMAGE_BASEPATH}/working-alumni.svg`;

const cardsConfig = [
    {
        id: 'claimedEntrepreneurs',
        label: 'Claimed Entrepreneurs',
        image: imgClaimed,
        imageWidth: 54,
        tooltip: 'Alumni whose work type is Entrepreneurship or who have a current Entrepreneurship job.',
    },
    {
        id: 'confirmedEntrepreneurs',
        label: 'Confirmed Entrepreneurs',
        image: imgConfirmed,
        imageWidth: 54,
        tooltip: 'Alumni with seniority Founders (verified) on a current Entrepreneurship job.',
    },
    {
        id: 'uniqueCompanies',
        label: 'Unique Companies',
        image: imgCompanies,
        imageWidth: 49,
        tooltip: 'Distinct Startup or Unicorn companies of Confirmed Entrepreneurs.',
    },
    {
        id: 'firstGenerationFounders',
        label: '1st Generation Founders',
        image: imgFirstGen,
        imageWidth: 49,
        tooltip: 'Founders (verified) marked as 1st Generation.',
    },
    {
        id: 'withFinancialData',
        label: 'With Financial Data',
        image: imgFinancial,
        imageWidth: 49,
        tooltip: 'Entrepreneurs whose Entrepreneurship company Operational Status is Registered with Financial Data.',
    },
];

const EntrepreneurSummaryCardControl = () => {
    const { state, dispatch } = useContext(SmartContext);
    const firstGenLabel = EntrepreneurDirectoryService.founderStatusLabel(
        FIRST_GENERATION_CODE,
        state,
        '1st Generation'
    );
    const summaryCards = cardsConfig.map((card) =>
        card.id === 'firstGenerationFounders'
            ? {
                  ...card,
                  label: `${firstGenLabel} Founders`,
                  tooltip: `Founders (verified) marked as ${firstGenLabel}.`,
              }
            : card
    );
    const summaryData = (state as any).data?.summary || {};
    const [searchName, setSearchName] = useState('');
    const [showResetButton, setShowResetButton] = useState(false);
    const [isShowSearch, setShowSearch] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    useEffect(() => {
        const defaultFilters = EntrepreneurDirectoryService.getDefaultSearchCriteria();
        const hasActiveFilter = JSON.stringify((state as any).data?.filters) !== JSON.stringify(defaultFilters);
        setShowResetButton(hasActiveFilter);
    }, [(state as any).data?.filters]);

    const students = (state as any).data?.students || [];
    const searchLower = searchName.toLowerCase();
    const nameMap = new Map<string, { text: string; student: any }>();
    const companyTags: string[] = [];

    // Same company as the directory card: the shown ENTREPRENEURSHIP job, not the profile's scalar job.
    const lookup = (id: any, domain: string) => getDomainValueForCode(id, domain, state);
    const shownCompany = (s: any) => {
        const { job } = pickDisplayJob(s.currentJobsJson, 'ENTREPRENEURSHIP');
        return job ? jobPlace(job, lookup).name : '';
    };

    students.forEach((s: any) => {
        const fullName = (s.name || formatFullName(s.firstName, s.lastName)).trim();
        if (fullName && fullName.toLowerCase().includes(searchLower)) {
            const nameKey = fullName.toLowerCase();
            if (!nameMap.has(nameKey)) nameMap.set(nameKey, { text: fullName, student: s });
        }
        // Backend name search matches startup/unicorn companies on ENTREPRENEURSHIP jobs, so suggest only those.
        parseCurrentJobs(s.currentJobsJson)
            .filter((job: any) => job.jobCategory === 'ENTREPRENEURSHIP' && (Number(job.isStartup) === 1 || Number(job.isUnicorn) === 1))
            .forEach((job) => {
                const company = (jobPlace(job, lookup).name || '').trim();
                if (company && company.toLowerCase().includes(searchLower)) companyTags.push(company);
            });
    });

    const nameSuggestions = Array.from(nameMap.values()).map(({ text, student: s }) => ({
        id: `name-${s.uuid || s.id}`,
        type: 'name',
        text,
        subtext: shownCompany(s),
    }));

    const seenCompany = new Set<string>();
    const companySuggestions = companyTags
        .filter((text) => {
            const key = text.toLowerCase();
            if (seenCompany.has(key)) return false;
            seenCompany.add(key);
            return true;
        })
        .slice(0, 5)
        .map((text, i) => ({
            id: `company-${i}-${text.toLowerCase()}`,
            type: 'company',
            text,
            subtext: 'Company',
        }));

    const seenSuggestionText = new Set<string>();
    const suggestions = [...nameSuggestions, ...companySuggestions]
        .filter((s) => {
            const key = s.text.toLowerCase();
            if (seenSuggestionText.has(key)) return false;
            seenSuggestionText.add(key);
            return true;
        })
        .slice(0, 10);

    const handleSuggestionClick = (text: string) => {
        setSearchName(text);
        setShowSuggestions(false);
        const criteria: any = { ...(state as any).data?.filters, name: text };
        EntrepreneurDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchName(val);
        setShowSuggestions(val.length > 0);
        debouncedSearch(val);
    };

    useEffect(() => {
        const tooltipEls = [].slice.call(document.querySelectorAll('.defaultTooltip'));
        const tips = tooltipEls.map((el: any) => new Tooltip(el));
        return () => tips.forEach((t: any) => t.dispose());
    });

    const getCardBorder = (cardId: string) => (cardId === (state as any).data?.selectedCard ? 'active' : '');

    const handleCardClick = (cardId: string) => {
        const currentFilters = (state as any).data?.filters || {};
        const count = summaryData[cardId];
        if (!count || count <= 0) {
            dispatch({
                type: 'REFRESH_DATA',
                payload: {
                    data: {
                        ...(state as any).data,
                        students: [],
                        selectedCard: cardId,
                        filters: { ...currentFilters, selectedCard: cardId },
                        totalCount: 0,
                        pagination: { page: 1, pageSize: 50, total: 0, hasMore: false },
                    },
                },
            });
            return;
        }
        const criteria = {
            ...EntrepreneurDirectoryService.getDefaultSearchCriteria(),
            ...currentFilters,
            ...EntrepreneurDirectoryService.getCriteriaForSelectedCard(cardId, state),
            selectedCard: cardId,
            context: {
                ...currentFilters.context,
                level: 'MY_SCHOOL',
                page: 1,
                pageSize: 50,
                selectedCard: cardId,
            },
        };
        EntrepreneurDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch, { selectedCard: cardId });
    };

    const debouncedSearch = debounce((value: string) => {
        const criteria: any = { ...(state as any).data?.filters, name: value };
        EntrepreneurDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);
    }, 200);

    const handleNameSearchClear = () => {
        setSearchName('');
        setShowSuggestions(false);
        debouncedSearch('');
    };

    const handleInputBlur = () => {
        setTimeout(() => setShowSuggestions(false), 200);
    };

    return (
        <section className="bg-red pt-3 pt-sm-5 pb-3 container-fluid" id="alumni_directory" data-aos="fade-down">
            <div className="max-1140 mx-auto">
                <div className="row alumni-directory">
                    <div className="col-lg-12 alumni-directory-header">
                        <div className="alumni-directory-header_search">
                            <h2
                                data-aos="fade-left"
                                data-aos-delay="300"
                                className="font-22 font-600 text-white mb-3 mb-sm-0 me-3 aos-init aos-animate">
                                Entrepreneur <span className="d-block d-sm-inline-block">Directory</span>
                            </h2>

                            <div className={`alumni-directorySearch-wrap ${isShowSearch ? 'show' : ''} position-relative`}>
                                <form className="alumni-directorySearch height-45" onSubmit={(e) => e.preventDefault()}>
                                    <button type="button" className="btn icon-search height-45"></button>
                                    <input
                                        className="form-control height-45"
                                        type="search"
                                        placeholder="Search by alumni name, UUID or company"
                                        aria-label="Search"
                                        value={searchName}
                                        onChange={handleInputChange}
                                        onBlur={handleInputBlur}
                                        onFocus={() => searchName.length > 0 && setShowSuggestions(true)}
                                    />
                                    <a className="btn-cross" onClick={handleNameSearchClear}></a>
                                </form>
                                {showSuggestions && (
                                    <div
                                        className="suggestions-dropdown position-absolute bg-white rounded shadow-sm w-100 mt-1"
                                        style={{ zIndex: 1050, top: '100%', left: 0, maxHeight: 250, overflowY: 'auto' }}>
                                        {suggestions.length > 0 ? (
                                            suggestions.map((s: any) => (
                                                <div
                                                    key={s.id}
                                                    className="suggestion-item px-3 py-2"
                                                    style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                                    onMouseDown={() => handleSuggestionClick(s.text)}>
                                                    <div className="font-14 font-500 text-dark">{s.text}</div>
                                                    {s.subtext && <div className="font-12 text-muted text-truncate">{s.subtext}</div>}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-muted font-14">No matching results</div>
                                        )}
                                    </div>
                                )}
                                <a
                                    className="btn-alumniSearchToggle cursor-pointer icon-search d-flex d-sm-none"
                                    onClick={() => setShowSearch(!isShowSearch)}></a>
                            </div>

                            {showResetButton && (
                                <div className={`alumni-directorySearch-wrap ${isShowSearch ? 'show' : ''}`}>
                                    <button
                                        type="button"
                                        className="ms-2 btn btn-white-border height-40 mt-0"
                                        onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                        <span>Reset</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="aldir_scroll myCampus active">
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5">
                        {summaryCards.map((card) => (
                            <div key={card.id} className="col mb-4 aldir_box">
                                <div
                                    className={`bg-white rounded alumni-directory-block h-100 aos-init aos-animate ${getCardBorder(card.id)}`}
                                    data-aos="fade-up"
                                    data-aos-delay="900"
                                    role="button"
                                    onClick={() => handleCardClick(card.id)}>
                                    <div className="number font-28 font-600 d-flex justify-content-between align-items-center">
                                        {summaryData[card.id] ?? 0}
                                        <i
                                            className="bi bi-info-circle-fill defaultTooltip font-16 position-absolute top-10 right-10"
                                            data-bs-toggle="tooltip"
                                            title={card.tooltip}
                                        />
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <div className="font-16 font-500 flex-1">{card.label}</div>
                                        <div className="alumni-directory-img me-3 mb-3">
                                            <img src={card.image} className="img-fluid" width={card.imageWidth} alt={card.label} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EntrepreneurSummaryCardControl;
